import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Loader2, Zap, Sparkles } from 'lucide-react'

const Loader = () => {
    return (
        <AnimatePresence>
            <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center"
            >
                <motion.div 
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.8, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="bg-white/95 backdrop-blur-md rounded-3xl shadow-2xl border border-gray-200 p-8 max-w-sm w-full mx-4"
                >
                    <div className="text-center">
                        {/* Animated Logo */}
                        <motion.div
                            animate={{ 
                                rotate: 360,
                                scale: [1, 1.1, 1]
                            }}
                            transition={{ 
                                rotate: { duration: 2, repeat: Infinity, ease: "linear" },
                                scale: { duration: 1, repeat: Infinity, ease: "easeInOut" }
                            }}
                            className="w-16 h-16 bg-gradient-to-br from-orange-400 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg"
                        >
                            <Zap className="w-8 h-8 text-white" />
                        </motion.div>

                        {/* Loading Spinner */}
                        <div className="relative mb-6">
                            <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                className="w-12 h-12 border-4 border-gray-200 border-t-orange-500 rounded-full mx-auto"
                            />
                            
                            {/* Pulsing Ring */}
                            <motion.div
                                animate={{ 
                                    scale: [1, 1.2, 1],
                                    opacity: [0.5, 0.2, 0.5]
                                }}
                                transition={{ 
                                    duration: 2, 
                                    repeat: Infinity, 
                                    ease: "easeInOut" 
                                }}
                                className="absolute inset-0 w-12 h-12 border-2 border-orange-300 rounded-full mx-auto"
                            />
                        </div>

                        {/* Loading Text */}
                        <motion.div
                            animate={{ opacity: [0.7, 1, 0.7] }}
                            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                        >
                            <h3 className="text-xl font-bold text-gray-800 mb-2">
                                Loading...
                            </h3>
                            <p className="text-gray-600 text-sm">
                                Please wait while we process your request
                            </p>
                        </motion.div>

                        {/* Animated Dots */}
                        <div className="flex justify-center gap-1 mt-4">
                            {[0, 1, 2].map((index) => (
                                <motion.div
                                    key={index}
                                    animate={{ 
                                        y: [0, -10, 0],
                                        scale: [1, 1.2, 1]
                                    }}
                                    transition={{ 
                                        duration: 0.6, 
                                        repeat: Infinity, 
                                        delay: index * 0.2,
                                        ease: "easeInOut" 
                                    }}
                                    className="w-2 h-2 bg-gradient-to-r from-orange-400 to-teal-500 rounded-full"
                                />
                            ))}
                        </div>

                        {/* Sparkle Effects */}
                        <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-3xl">
                            {[...Array(6)].map((_, index) => (
                                <motion.div
                                    key={index}
                                    animate={{
                                        x: [0, 20, 0],
                                        y: [0, -20, 0],
                                        opacity: [0, 1, 0],
                                        scale: [0, 1, 0]
                                    }}
                                    transition={{
                                        duration: 3,
                                        repeat: Infinity,
                                        delay: index * 0.5,
                                        ease: "easeInOut"
                                    }}
                                    className="absolute"
                                    style={{
                                        left: `${20 + (index * 15)}%`,
                                        top: `${30 + (index * 10)}%`
                                    }}
                                >
                                    <Sparkles className="w-4 h-4 text-orange-400" />
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    )
}

export default Loader